<template>
  <div
    ref="queryPanel"
    class="query-panel"
  >
    <Modal
      v-model="showFileDragError"
      :mask-closable="false"
    >
      <p
        slot="header"
        class="dialog-window"
      >
        <i class="fas fa-exclamation-triangle button-icon" />
        <span style="font-size: 16px">Warning!</span>
      </p>
      <div>You do not have permissions to drag the selected file into the targeted folder!</div>
      <div
        slot="footer"
        class="footer-buttons"
      >
        <Button
          type="info"
          size="large"
          style="width: 80px"
          @click="showFileDragError = false"
        >
          OK
        </Button>
      </div>
    </Modal>

    <div class="panel-main">
      <div class="panel-icon">
        <Tooltip
          content="Workspace"
          placement="right"
          class="icon-block"
          :class="{'active': currentPanel === 'workspace'}"
          @click.native="showPanel('workspace')"
        >
          <i class="far fa-folder-open action-icon" />
        </Tooltip>
        <Tooltip
          content="Search"
          placement="right"
          class="icon-block"
          :class="{'active': currentPanel === 'search'}"
          @click.native="showPanel('search')"
        >
          <i class="fas fa-search action-icon" />
        </Tooltip>
        <Tooltip
          content="Schema Tree"
          placement="right"
          class="icon-block"
          :class="{'active': currentPanel === 'dataCatalog'}"
          @click.native="showPanel('dataCatalog')"
        >
          <i class="fas fa-database action-icon" />
        </Tooltip>
        <Tooltip
          content="Query History"
          placement="right"
          class="icon-block"
          :class="{'active': currentPanel === 'history'}"
          @click.native="showPanel('history')"
        >
          <i class="fas fa-history action-icon" />
        </Tooltip>
        <Tooltip
          content="Scheduled Queries"
          placement="right"
          class="icon-block"
          :class="{'active': currentPanel === 'scheduled'}"
          @click.native="showPanel('scheduled')"
        >
          <i class="fas fa-calendar-check action-icon" />
        </Tooltip>
      </div>
      <div class="panel-content">
        <Scroll
          v-show="currentPanel === 'workspace'"
          id="libraryScroll"
          ref="libraryScroll"
          :height="panelHeight"
          :distance-to-edge="scrollEdgeDistances"
        >
          <Spin
            v-show="workspaceLoading"
            fix
          >
            <Icon
              type="ios-loading"
              size="18"
              class="spin-icon-load"
            />
            <div>Loading</div>
          </Spin>
          <template v-show="!workspaceLoading && queryWorkspace.length">
            <SlVueTree
              ref="slVueTree"
              v-model="workspaceRoot.children"
              :allow-multiselect="false"
              @drop="dropHandler"
              @beforedrop="beforeDropHandler"
              @select="selectFileNode"
              @nodedblclick="fileNodeDblClick"
              @toggle="folderNodeToggle"
            >
              <template
                slot="toggle"
                slot-scope="{ node }"
              >
                <span
                  v-if="!node.isLeaf"
                  class="item-icon toggle"
                >
                  <i
                    v-if="!node.isExpanded"
                    class="fas fa-caret-right"
                  />
                  <i
                    v-else-if="node.isExpanded"
                    class="fas fa-caret-down"
                  />
                </span>
              </template>
              <template
                slot="title"
                slot-scope="{ node }"
              >
                <span :class="{ 'item-icon': true, 'file': node.isLeaf }">
                  <i
                    v-if="node.data.isRepo && node.data.isRoot"
                    class="fab fa-google-drive"
                  />
                  <i
                    v-else-if="node.data.linkedToRepo"
                    class="fas fa-link"
                  />
                  <i
                    v-else-if="node.isLeaf"
                    class="far fa-file"
                  />
                  <i
                    v-else-if="!node.isLeaf && (!node.isExpanded || !node.children.length)"
                    class="fas fa-folder"
                  />
                  <i
                    v-else-if="!node.isLeaf && node.isExpanded && node.children.length"
                    class="fas fa-folder-open"
                  />
                </span>
                <Tooltip
                  v-if="node.data.tooltipText"
                  :content="node.data.tooltipText"
                  :max-width="200"
                  placement="right"
                  transfer
                >
                  {{ node.title }}
                </Tooltip>
                <span v-else>
                  {{ node.title }}
                </span>
              </template>
            </SlVueTree>
          </template>
        </Scroll>

        <div v-show="currentPanel === 'search'">
          <SearchPanel
            :height="panelHeight"
            :workspace-tree="workspaceRoot"
            @node-select="selectFileNode"
            @node-dbl-click="fileNodeDblClick"
          />
        </div>

        <Scroll
          v-show="currentPanel === 'dataCatalog'"
          id="schemaTreeScroll"
          ref="schemaTreeScroll"
          :height="panelHeight"
          :distance-to-edge="scrollEdgeDistances"
        >
          <Spin
            v-show="schemaTreeLoading"
            fix
          >
            <Icon
              type="ios-loading"
              size="18"
              class="spin-icon-load"
            />
            <div>Loading</div>
          </Spin>
          <template v-show="!schemaTreeLoading">
            <div>
              <QueryPreview
                ref="dataCatalogPreview"
                :show="dataCatalogPreviewData.show"
                :title="dataCatalogPreviewData.title"
                :is-query="dataCatalogPreviewData.isQuery"
                :content="dataCatalogPreviewData.content"
                :width="dataCatalogPreviewData.width"
                :height="dataCatalogPreviewData.height"
                :horizontal-position="dataCatalogPreviewData.horizontalPosition"
                :horizontal-offset="dataCatalogPreviewData.horizontalOffset"
                :referred-top-position="dataCatalogPreviewData.referredTopPosition"
              />
              <SchemaTree
                v-model="snowflakeSchema"
                arrows-type="caret"
                show-search-box
                @schemaPath="getSchemaPath"
                @showColumnDetails="toggleColumnDetails"
              />
            </div>
          </template>
        </Scroll>

        <Scroll
          v-show="currentPanel === 'history'"
          ref="historyScroll"
          :height="panelHeight"
          :distance-to-edge="scrollEdgeDistances"
          :on-reach-bottom="historyQueriesDisplayed < queryHistory.length ? loadQueryHistory : null"
        >
          <Spin
            v-show="historyLoading"
            fix
          >
            <Icon
              type="ios-loading"
              size="18"
              class="spin-icon-load"
            />
            <div>Loading</div>
          </Spin>
          <template v-if="!historyLoading && queryHistory.length === 0">
            <p>No Queries in History</p>
          </template>
          <template v-show="!historyLoading && queryHistory.length > 0">
            <div>
              <QueryPreview
                ref="historyPreview"
                :show="historyQueryPreviewData.show"
                :title="historyQueryPreviewData.title"
                :is-query="historyQueryPreviewData.isQuery"
                :content="historyQueryPreviewData.content"
                :width="historyQueryPreviewData.width"
                :height="historyQueryPreviewData.height"
                :horizontal-position="historyQueryPreviewData.horizontalPosition"
                :horizontal-offset="historyQueryPreviewData.horizontalOffset"
                :referred-top-position="historyQueryPreviewData.referredTopPosition"
              />
              <Timeline>
                <TimelineItem
                  v-for="(queryInfo, index) in queryHistoryShown"
                  :key="index"
                  :ref="`query-group-${index}`"
                  :name="`query-history-${index}`"
                >
                  <Icon
                    slot="dot"
                    type="ios-calendar"
                    size="18"
                    style="color: var(--color-black)"
                  />
                  <p class="time">
                    {{ queryInfo.date }}
                  </p>
                  <p class="content">
                    <span
                      v-for="(queryItem, queryIndex) in queryInfo.items"
                      :key="queryIndex"
                      :ref="`query-${index}-${queryIndex}`"
                      :style="{ height: `${historyItemHeight}px` }"
                      @dblclick="copyQueryToPad({ queryId: queryItem.id, queryText: queryItem.queryText, isQueryWorkspace: false }, true, { createNewTab: false, tabName: '' })"
                      @mouseover="onUpdatePreviewProps(true, { queryId: queryItem.id, queryText: queryItem.queryText, isQueryWorkspace: false }, 'queryPanel', `query-${index}-${queryIndex}`)"
                      @mouseleave="onUpdatePreviewProps(false)"
                    >
                      {{ queryItem.occuredAtTime }}
                    </span>
                  </p>
                </TimelineItem>
              </Timeline>
            </div>
          </template>
        </Scroll>
        <ScheduledQueryPanel
          v-show="currentPanel === 'scheduled'"
          :query-panel-width="queryPanelWidth"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SchemaTree from './query-panel/SchemaTree'
import SearchPanel from './query-panel/SearchPanel'
import SlVueTree from 'sl-vue-tree'
import ScheduledQueryPanel from '@/components/helper-components/scheduled-query/ScheduledQueryPanel'
import QueryPreview from '@/components/helper-components/QueryPreview'

import { mapState } from 'vuex'
import _ from 'lodash'
import '@/styles/sl-vue-tree.css'

export default {
  components: {
    SchemaTree,
    SlVueTree,
    SearchPanel,
    ScheduledQueryPanel,
    QueryPreview
  },

  props: {
    userId: Number,
    queryWorkspaceLoading: Boolean,
    queryHistory: Array,
    queryHistoryShown: Array,
    queryHistoryLoading: Boolean,
    historyQueriesDisplayed: Number,
    historyItemHeight: Number,
    queryLoadMin: Number,
    panelHeight: Number,
    snowflakeData: Object,
    users: Array,
    queryPanelWidth: Number
  },

  data () {
    return {
      showFileDragError: false,
      workspaceLoading: this.queryWorkspaceLoading,
      historyLoading: this.queryHistoryLoading,
      scrollEdgeDistances: [20, 20],
      selectedScheduledQuery: {},
      schemaTreeLoading: true,
      currentPanel: this.$root.preferences.queryPad.currentPanelTab || 'workspace',
      columnDetails: {
        name: '',
        dataType: '',
        description: ''
      },
      snowflakeSchema: [],
      labelDataCatalog: (h) => {
        return h('div', {
          style: {
            'width': '100%',
            'display': 'flex',
            'align-items': 'center',
            'justify-content': 'center'
          }
        }, [
          h('i', {
            style: {
              'font-size': '16px',
              'padding-left': '0',
              'padding-right': '0'
            },
            class: 'fas fa-database'
          })
        ])
      },
      labelQueryHistory: (h) => {
        return h('div', {
          style: {
            'width': '100%',
            'display': 'flex',
            'align-items': 'center',
            'justify-content': 'center'
          }
        }, [
          h('i', {
            style: {
              'font-size': '16px',
              'padding-left': '0',
              'padding-right': '0'
            },
            class: 'fas fa-history'
          })
        ])
      },
      labelQueryWorkspace: (h) => {
        return h('div', {
          style: {
            'width': '100%',
            'display': 'flex',
            'align-items': 'center',
            'justify-content': 'center'
          }
        }, [
          h('i', {
            style: {
              'font-size': '16px',
              'padding-left': '0',
              'padding-right': '0'
            },
            class: 'far fa-folder-open'
          })
        ])
      },
      userWorkspaceTree: {
        title: 'My Workspace',
        isDraggable: false,
        isLeaf: false,
        isExpanded: true,
        isSelectable: true,
        folderPath: '/',
        children: [],
        isRoot: true,
        data: {
          isRoot: true,
          tooltipText: 'Locally saved queries (private to you)'
        }
      },
      queryRepoTree: {
        title: 'Shared Workspace',
        isDraggable: false,
        isLeaf: false,
        isExpanded: true,
        isSelectable: false,
        folderPath: '/shared-workspace/',
        children: [],
        isRoot: true,
        isRepo: true,
        data: {
          isRoot: true,
          isRepo: true,
          tooltipText: 'Shared Queries (public to everyone)'
        }
      },
      workspaceRoot: {
        isDraggable: false,
        isLeaf: false,
        isExpanded: true,
        folderPath: '',
        children: []
      },
      userWorkspaceExpandedFolders: {},
      queryRepoExpandedFolders: {},
      folderError: '',
      historyQueryPreviewData: { // Static data
        title: 'Query Preview',
        isQuery: true,
        content: {},
        width: 632,
        height: 300,
        horizontalPosition: '',
        horizontalOffset: this.queryPanelWidth - 40,
        referredTopPosition: 0,
        show: false
      },
      dataCatalogPreviewData: {
        title: '',
        isQuery: true,
        content: {},
        width: 632,
        height: 300,
        horizontalPosition: '',
        horizontalOffset: this.queryPanelWidth - 40,
        referredTopPosition: 0,
        show: false
      }
    }
  },

  computed: {
    queryRepoFiles () {
      return this.queryRepo ? this.queryRepo.files : []
    },

    queryRepoFolders () {
      return this.queryRepo ? this.queryRepo.folders : []
    },

    ...mapState('queryRepo', {
      queryRepo: (state, getters) => getters.queryRepo
    }),

    ...mapState('queryWorkspace', {
      queryWorkspace: state => state.workspace,
      queryWorkspaceFolders: state => state.folders
    }),

    ...mapState('queryPad', {
      selectedFileNode: state => state.selectedFileNode
    }),

    ...mapState('scheduledQuery', {
      scheduledQueries: 'scheduledQueries'
    })
  },

  watch: {
    queryHistoryShown () {
      this.historyLoading = false
    },

    snowflakeData (snowflakeData) {
      this.setDataBaseSchema(snowflakeData)
    },

    queryRepo: {
      handler () {
        this.createRootWorkspace()
      },
      deep: true
    },

    queryWorkspace: {
      handler () {
        this.createRootWorkspace()
      },
      deep: true
    },

    queryWorkspaceFolders: {
      handler () {
        this.createRootWorkspace()
      },
      deep: true
    }
  },

  mounted () {
    this.setDataBaseSchema(this.snowflakeData)

    if (this.queryWorkspace.length) {
      this.createRootWorkspace()
    }

    if (this.queryHistoryShown.length) {
      this.loadQueryHistory()
    }

    this.$store.dispatch('scheduledQuery/onLoadScheduledQueries', this.userId)
    this.$store.dispatch('scheduledQuery/onLoadScheduledQueriesSubscriptions', this.userId)

    this.toggleColumnDetails(false)
  },

  methods: {
    /**
     * Makes a commit to the Vuex store to update the currently selected file node on query pad
     * @param {Object} fileNode - The file node that is selected
     */
    setSelectedFileNode (fileNode) {
      this.$store.commit('queryPad/setSelectedFileNode', fileNode)
    },

    /**
     * Sets the items to display from library according with the maximum elements to display per block.
     * @param {Number} loadLimit
     */
    loadQueryWorkspace (loadLimit) {
      this.$emit('loadQueryWorkspace', loadLimit)
    },

    /**
     * Creates the root folder and its content.
     */
    createRootWorkspace () {
      this.workspaceLoading = true
      this.userWorkspaceTree.children = this.createUserWorkspace()
      this.queryRepoTree.children = this.createQueryRepoSpace()
      const linkedFolder = this.createLinkedFolder(this.queryRepoTree)
      this.userWorkspaceTree.children.unshift(linkedFolder)
      const children = [this.userWorkspaceTree, this.queryRepoTree]
      this.$set(this.workspaceRoot, 'children', children)
      this.workspaceLoading = false
    },

    /**
     * Creates the linked folder (to Shared Workspace) for the local workspace
     * @param {Object} queryRepo - The query repository in which we want to create a linked folder from
     * @return {Object} - The linked folder object
     */
    createLinkedFolder (queryRepo) {
      const linkedFolder = {
        title: 'Shared Queries',
        isDraggable: false,
        isLeaf: false,
        isExpanded: this.userWorkspaceExpandedFolders['/Shared Queries/'] || false,
        folderPath: '/Shared Queries/',
        linkedToRepo: true,
        children: [],
        data: {
          linkedToRepo: true,
          folderPath: '/Shared Queries/',
          tooltipText: 'This folder is linked to your own "Users" folder within the Shared Workspace'
        }
      }
      const userFolder = this.getQueryRepoUserFolder(queryRepo)

      if (userFolder) {
        linkedFolder.children = userFolder.children
      }

      return linkedFolder
    },

    /**
     * Gets the parent user folder of a folder node, if it belongs to a parent folder
     * @param {Object} folderNode
     * @return {Object} - Returns the user folder object or undefined if it doesn't belong to a user folder
     */
    getParentUserFolder (folderNode) {
      const usersFolder = this.getQueryRepoUsersFolder(this.workspaceRoot.children[1])

      return usersFolder && usersFolder.children
        .filter(child => !child.isLeaf)
        .find(userFolder => this.isFolderInFolder(folderNode, userFolder))
    },

    /**
     * Recursively checks to see if a folder is found within another folder
     * @param {Object} targetFolderNode
     * @param {Object} currentFolderNode
     * @return {Boolean}
     */
    isFolderInFolder (targetFolderNode, currentFolderNode) {
      const { data: { folder: targetFolder } } = targetFolderNode
      const { data: { folder: currentFolder } } = currentFolderNode

      if (targetFolder.id === currentFolder.id) {
        return true
      }

      return (currentFolderNode.children || [])
        .filter(child => !child.isLeaf)
        .some(folder => this.isFolderInFolder(targetFolderNode, folder))
    },

    /**
     * Gets the "Users" folder from the Query Repo
     * @param {Object} queryRepo
     * @return {Object}
     */
    getQueryRepoUsersFolder (queryRepo) {
      const { children = [] } = queryRepo

      return children.find(child => {
        const { folder } = child

        return folder && !folder.editable && !folder.insertable && folder.name.toLowerCase() === 'users'
      })
    },

    /**
     * Retrieves the current user's personal folder on the Query Repository
     * @param {Object} queryRepo - The query repository that we are retrieving the user folder from
     * @return {Object | null} - The user folder, null if it does not currently exist.
     */
    getQueryRepoUserFolder (queryRepo) {
      const usersFolder = this.getQueryRepoUsersFolder(queryRepo)

      if (usersFolder) {
        const userEmail = this.$auth.getUser().email.toLowerCase()
        const emailFolder = usersFolder.children.find(child => child.title.toLowerCase() === userEmail)

        if (emailFolder) {
          return emailFolder
        }
      }

      return null
    },

    /**
     * Creates the User Workspace Tree data for rendering the Local Workspace tree
     * @return {Array} - The workspace tree
     */
    createUserWorkspace () {
      const userWorkspace = {
        children: [],
        folderPath: '/'
      }

      // Set up Query Workspace
      this.queryWorkspace.forEach(query => {
        if (query.queryName.charAt(0) !== '/') {
          query.queryName = `/${query.queryName}`
        }
        const queryPath = query.queryName.split('/')
        const folderNode = this.getFolderNodeFromPath(queryPath, userWorkspace)
        const newFile = {
          title: queryPath[queryPath.length - 1],
          isDraggable: true,
          isLeaf: true,
          query: query,
          folderPath: folderNode.folderPath,
          filePath: queryPath[queryPath.length - 1],
          data: { query }
        }
        folderNode.children.push(newFile)
      })

      this.queryWorkspaceFolders.forEach(folder => {
        let folderPathStr = folder.path

        if (folderPathStr.charAt(0) !== '/') {
          folderPathStr = `/${folderPathStr}`
        }

        const folderPathArr = folderPathStr.split('/')
        const folderNode = this.getFolderNodeFromPath(folderPathArr, userWorkspace)
        const existingFolderNode = folderNode.children.find(child => !child.isLeaf && child.title === folder.name)

        if (!existingFolderNode) {
          const folderPath = `${folderNode.folderPath}${folder.name}/`
          const newFolder = {
            title: folder.name,
            isDraggable: false,
            isLeaf: false,
            isExpanded: false,
            folderPath,
            children: [],
            data: { folderPath }
          }
          folderNode.children.push(newFolder)
        }
      })

      this.sortChildrenNodesRecursive(userWorkspace)

      return userWorkspace.children
    },

    /**
     * Creates the Query Repo Tree data for rendering the Shared Workspace Tree
     * @return {Array} - The Query Repo Tree
     */
    createQueryRepoSpace () {
      if (!this.queryRepo) {
        return
      }

      const folderArr = []
      const fileArr = []
      const { files, folders } = this.queryRepo

      folders.forEach(folder => this.addQueryRepoFolderNode(folder, folderArr))
      files.forEach(file => this.addQueryRepoFileNode(file, fileArr))
      this.sortQueryRepoFolders(folderArr)
      this.sortQueryRepoFiles(fileArr)

      const nodeArr = [...folderArr, ...fileArr]
      const fileTree = this.createQueryRepoSpaceHelper(
        nodeArr.filter(node => node.parentId === null),
        nodeArr
      )

      this.setFolderPathInFileTree(fileTree, '/')

      return fileTree
    },

    /**
     * Adds a folder node to nodes array
     * @param {Object} folder - The folder object whose information will be stored in node object
     * @param {Array} treeNodes - The array where we want to add a new folder node to
     */
    addQueryRepoFolderNode (folder, treeNodes) {
      const { id, name, parentId } = folder

      treeNodes.push({
        id,
        parentId,
        title: name,
        isDraggable: false,
        isLeaf: false,
        isExpanded: this.queryRepoExpandedFolders[id] || false,
        isRepo: true,
        folder,
        data: {
          folder,
          isRepo: true
        }
      })
    },

    /**
     * Adds a file node to nodes array
     * @param {Object} file - The file object whose info will be stored in node object
     * @param {Array} nodes - The array where wr want to add a new file node to
     */
    addQueryRepoFileNode (file, treeNodes) {
      const { id, queryName, folderId } = file

      treeNodes.push({
        id,
        parentId: folderId,
        title: queryName,
        isDraggable: true,
        isLeaf: true,
        query: file,
        filePath: queryName,
        isRepo: true,
        data: {
          query: file,
          isRepo: true
        }
      })
    },

    /**
     * Helper function to recursively create file tree structure for Shared Workspace
     * @param {Array} parents - The array of tree nodes that are located at the top level of the sub tree
     * this recursive function is creating.
     * @param {Array} treeNodes - The array of all nodes within the entire Shared Workspace tree. Essentially
     * all nodes within the Query Repo
     * @return {Array} - The file tree
     */
    createQueryRepoSpaceHelper (parents, treeNodes) {
      return parents.map(parent => {
        if (parent.isLeaf) {
          return { ...parent }
        }

        return {
          ...parent,
          children: this.createQueryRepoSpaceHelper(treeNodes.filter(treeNode => treeNode.parentId === parent.id), treeNodes)
        }
      })
    },

    /**
     * Sets folder path for each node within the file tree recursively
     * @param {Array} fileTree - The file tree whose nodes we are setting their folder paths for
     * @param {String} folderPath - The folder path so far given our context of where this recursive function
     * is called within the file tree
     */
    setFolderPathInFileTree (fileTree, folderPath) {
      fileTree.forEach(treeNode => {
        if (treeNode.isLeaf) {
          treeNode.folderPath = folderPath
          treeNode.filePath = treeNode.title
        } else {
          const nodeFolderPath = `${folderPath}${treeNode.title}/`
          treeNode.folderPath = nodeFolderPath
          this.setFolderPathInFileTree(treeNode.children, nodeFolderPath)
        }
      })
    },

    /**
       * Sets the items to display from history according with the maximum elements to display per block.
       * @param {Number} loadLimit
       */
    loadQueryHistory (loadLimit) {
      this.$emit('loadQueryHistory', loadLimit)
    },

    /**
     * Gets the folder node from a given path in order to make some changes into it.
     * NOTE: Creates new folder nodes if it is indicated in the path and does not currently exist.
     * @param {Array} path The path of the selected file or folder.
     * @param {Object} workspace - The workspace in which the folder is located within
     * @return {Object} The parent folder of the selected file or folder.
     */
    getFolderNodeFromPath (path, workspace) {
      let folderNode = workspace
      for (let i = 1; i < path.length - 1; i++) {
        let node = folderNode.children.find(folder => folder.title === path[i] && !folder.isLeaf)

        if (!node) {
          const parentFolder = folderNode.folderPath || '/'
          const folderPath = `${parentFolder}${path[i]}/`
          const newFolder = {
            title: path[i],
            isDraggable: false,
            isLeaf: false,
            isExpanded: this.userWorkspaceExpandedFolders[folderPath] || false,
            folderPath,
            children: [],
            data: {
              folderPath
            }
          }
          folderNode.children.push(newFolder)
          node = folderNode.children[folderNode.children.length - 1]
        }
        folderNode = node
      }

      return folderNode
    },

    /**
     * Defines the Data Catalog schema to display on panel.
     * @param {Array} dataSchema
     */
    async setDataBaseSchema (dataSchema) {
      this.schemaTreeLoading = true
      if (!dataSchema) {
        return
      } else if (!dataSchema.tables) {
        this.schemaTreeLoading = false
        return
      }

      let databaseSchema = []
      let databaseIndex = 0
      let schemaIndex = 0
      dataSchema.tables.forEach(item => {
        const databaseExists = this.snowflakeSchema.length > 0 && this.snowflakeSchema[databaseIndex].title === item.databaseName

        if (!databaseExists) {
          const databaseName = {
            title: item.databaseName,
            type: 'database',
            expand: true,
            icon: 'fas fa-database',
            schemaView: []
          }

          this.snowflakeSchema.push(databaseName)
          databaseIndex = this.snowflakeSchema.length - 1
          databaseSchema = this.snowflakeSchema[databaseIndex].schemaView
        }

        const isTable = item.kind.toLowerCase() === 'table'
        const groupKind = isTable ? 'Tables' : 'Views'

        const columnsData = item.columns.map(column => {
          const description = column.description || ''
          return {
            title: column.name,
            type: 'column',
            dataType: column.type,
            description,
            icon: 'fas fa-genderless'
          }
        })
        const tableData = {
          title: item.name,
          type: item.kind.toLowerCase(),
          expand: false,
          icon: isTable ? 'fas fa-table' : 'fas fa-eye',
          schemaView: [],
          schemaContent: columnsData
        }

        const schemaExists = databaseSchema.length > 0 && databaseSchema[schemaIndex].title === item.schemaName

        if (!schemaExists) {
          const newItem = {
            title: item.schemaName,
            type: 'schema',
            expand: false,
            icon: 'fas fa-book',
            schemaView: []
          }
          databaseSchema.push(newItem)
          schemaIndex = databaseSchema.length - 1
        }

        const tableGroup = databaseSchema[schemaIndex].schemaView.find(item => item.title === groupKind)

        if (!tableGroup) {
          const newItem = {
            title: groupKind,
            type: 'object',
            expand: false,
            excludeFromPath: true,
            schemaView: [],
            schemaContent: [
              tableData
            ]
          }
          databaseSchema[schemaIndex].schemaView.push(newItem)
        } else {
          tableGroup.schemaContent.push(tableData)
        }
      })
      this.schemaTreeLoading = false
    },

    /**
     * Receives the schema path from Data Catalog and sends it to the Query Pad (parent compomnent)
     * in iorder to be displayed into the current pad.
     * @param {String} path
     */
    getSchemaPath (path) {
      this.$emit('schemaPath', path)
    },

    updateQueryPreviewData (newData) {
      this.historyQueryPreviewData = {
        ...this.historyQueryPreviewData,
        ...newData
      }
    },

    updatePreviewreferredTop (newTop) {
      this.$set(this.historyQueryPreviewData, 'referredTopPosition', newTop)
    },

    /**
     * Sets the top position for the query preview.
     * @param {Boolean} showPreview      If "TRUE", displays the query preview. Otherwise, hides the query preview.
     * @param {Object} queryObject       It contains query properties (id, queryText, isLibrary, queryName).
     * @param {Object} parentDomName  name of upper dom object, used for calculate top value (relative -> absolute)
     * @param {Array} childDomName name of a list of child doms that the QueryPreview renders on
     */
    onUpdatePreviewProps (showPreview, queryObject = {}, parentDomName, childDomName) {
      // TODO: if showPreview === false, simplify to only hide the preview (mouseoff)
      const parentDom = this.$refs[parentDomName]
      const childDomArr = this.$refs[childDomName]
      const parentTop = parentDom ? parentDom.getBoundingClientRect().top : 0
      if (childDomArr) {
        const newTop = childDomArr[0].getBoundingClientRect().top - parentTop
        this.updatePreviewreferredTop(newTop)
      }

      const isEnoughHeight = this.historyQueryPreviewData.referredTopPosition + this.historyQueryPreviewData.height + parentTop < window.innerHeight

      // Construct the updated queryPreviewDta
      // title, isQuery is static, not changed here
      let horizontalPosition = ''
      let top = this.historyQueryPreviewData.referredTopPosition
      let height = this.historyQueryPreviewData.height
      const isQuery = this.historyQueryPreviewData.isQuery
      const show = showPreview

      if (isEnoughHeight || (!isEnoughHeight && top < height)) {
        horizontalPosition = 'right-start'
      } else {
        horizontalPosition = 'right-end'
        top = top - height - (isQuery ? 32 : 24)
      }

      height = isQuery ? 300 : 112
      const queryText = isQuery && queryObject.hasOwnProperty('queryText') ? queryObject.queryText : ''
      const content = { ...this.historyQueryPreviewData.content, queryText }

      const dataParam = { referredTopPosition: top, height, horizontalPosition, show, isQuery, content }
      this.updateQueryPreviewData(dataParam)
    },

    /**
     * Copies the previewed query into the current tab or into a new tab.
     * @param {Object} queryObject
     * @param {Boolean} validateQuery
     * @param {Object} newTabProperties
     */
    copyQueryToPad (queryObject, validateQuery, newTabProperties) {
      this.$emit('copyQueryToPad', queryObject, validateQuery, newTabProperties)
    },

    /**
     * Checks to see if the current user has permissions to drag the file node
     * @param {Object} fileNode
     * @return {Boolean}
     */
    canUserDragFileNode (fileNode) {
      const { data: { isRepo, query } } = fileNode

      if (isRepo && query) {
        const folderNode = this.findQueryRepoFolder(query.folderId, this.workspaceRoot.children[1])

        if (folderNode) {
          return this.canUserModifyFolderContents(folderNode)
        }
      }

      return !isRepo
    },

    /**
     * Determines whether or not the user can insert or remove contents from folder
     * @param {Object} folderNode
     * @return {Boolean}
     */
    canUserModifyFolderContents (folderNode) {
      const { data: { isRepo, isRoot, folder, query } } = folderNode

      if (folder) {
        const parentUserFolder = this.getParentUserFolder(folderNode)
        const isRepoRoot = isRoot && isRepo
        const isFolderInsertable = folder.insertable
        const isFolderInOtherUserFolder = parentUserFolder && _.has(parentUserFolder, 'data.folder.userId') && parentUserFolder.folder.userId !== this.userId

        return !isRepo || (!isRepoRoot && isFolderInsertable && !isFolderInOtherUserFolder)
      } else if (isRepo && query) {
        const parentFolderNode = this.findQueryRepoFolder(query.folderId, this.workspaceRoot.children[1])

        if (parentFolderNode) {
          return this.canUserModifyFolderContents(parentFolderNode)
        }
      }

      // If destination node is not repo, then user can safely insert
      return !isRepo
    },

    /**
     * Handler for before tree node is dropped
     * @param {Array} draggedFileNodes - Array of file nodes that are being dragged
     * @param {Object} destinationFolder - The folder in which the file node(s) is dragged to
     * @param {Function} cancel - The function used to cancel a drop
     */
    beforeDropHandler (draggedFileNodes, destinationFolder, cancel) {
      if (!this.canUserDragFileNode(draggedFileNodes[0]) || !this.canUserModifyFolderContents(destinationFolder.node)) {
        this.showFileDragError = true
        cancel()
      }
    },

    /**
     * Drops selected items into a folder.
     * Notes:
     * - If the folder was collapsed, it will expand it.
     * - Once the items has been dropped, it will sort them by folders and files.
     * @param {Array} draggedFileNodes
     * @param {Object} destinationFolder
     */
    dropHandler (draggedFileNodes, destinationFolder) {
      const treePath = destinationFolder.node.path
      const dropFolder = this.getClosestFolderNodeInfo(this.workspaceRoot, treePath, 0)

      dropFolder.isExpanded = dropFolder.children.length > 0
      this.sortChildrenNodes(dropFolder)

      // Since user has to select a file node to drag it, we can assume that
      // this.selectedFileNode is the dragged file
      const draggedFile = {
        ...this.selectedFileNode,
        folderPath: dropFolder.folderPath,
        folderName: dropFolder.title
      }

      this.setSelectedFileNode(draggedFile)

      if (destinationFolder.node.data.linkedToRepo) {
        this.$emit('onRelocateQueryFile', { ...draggedFile.query, isRepo: draggedFile.isRepo }, { linkedToRepo: true })
      } else if (destinationFolder.node.data.isRepo) {
        let folder = null

        if (_.has(destinationFolder, 'node.data.folder')) {
          folder = destinationFolder.node.data.folder
        } else if (_.has(destinationFolder, 'node.data.query')) {
          folder = this.findQueryRepoFolder(destinationFolder.node.data.query.folderId, this.workspaceRoot.children[1])
        }

        if (folder) {
          this.$emit('onRelocateQueryFile', { ...draggedFile.query, isRepo: draggedFile.isRepo }, { ...folder, isRepo: true })
        }
      } else {
        this.$set(draggedFile.query, 'queryName', `${dropFolder.folderPath}${draggedFile.title}`)
        this.setSelectedFileNode(draggedFile)
        this.$emit('onRelocateQueryFile', { ...draggedFile.query, isRepo: draggedFile.isRepo })
      }
    },

    /**
     * Get the information of the selected node in the SlVueTree. If the selected node is a folder,
     * it is stored into the "selectedFileNode" var. Otherwise, gets the parent folder info.
     * @param {Array} selectedFileNodes
     */
    selectFileNode (selectedFileNodes) {
      this.setSelectedFileNode(null)

      if (selectedFileNodes.length !== 1) {
        return
      }

      if (selectedFileNodes[0].data.isRepo) {
        const { query, folder } = selectedFileNodes[0].data

        if (query) {
          this.setSelectedFileNode(this.findQueryRepoFile(query.id, this.workspaceRoot.children[1]))
        } else if (folder) {
          this.setSelectedFileNode(this.findQueryRepoFolder(folder.id, this.workspaceRoot.children[1]))
        }
      } else {
        const treePath = selectedFileNodes[0].path
        this.setSelectedFileNode(this.getFileNodeInfo(this.workspaceRoot, treePath, 0))
      }

      if (this.selectedFileNode && !this.selectedFileNode.isLeaf) {
        this.sortChildrenNodes(this.selectedFileNode)
      }
    },

    /**
     * Handler for when a folder node is toggled.
     * @param {Object} folderNode - The folder being toggle
     */
    folderNodeToggle (folderNode) {
      const { isLeaf } = folderNode
      const { isRepo, isRoot } = folderNode.data

      if (isLeaf || isRoot) {
        return
      }

      const { isExpanded } = folderNode

      if (isRepo) {
        const { folder } = folderNode.data
        this.queryRepoExpandedFolders[folder.id] = !isExpanded
      } else {
        const { folderPath } = folderNode.data
        this.userWorkspaceExpandedFolders[folderPath] = !isExpanded
      }
    },

    /**
     * Validates the selected node to know if it should be copied into the pad or not.
     * Notes:
     * - If the current node is a folder, will do nothing.
     * - If the current node is a file, then it will be copied into the pad
     * @param {Object} fileNode
     */
    fileNodeDblClick (fileNode) {
      const { title, isLeaf, data: { query, isRepo = false } } = fileNode
      if (!isLeaf) {
        return
      }

      const queryInfo = {
        queryId: query.id,
        queryName: query.queryName,
        queryText: query.queryText,
        isQueryWorkspace: true,
        label: title,
        isRepo
      }
      const tabInfo = {
        createNewTab: true,
        tabName: title
      }

      this.copyQueryToPad(queryInfo, false, tabInfo)
    },

    /**
     * Finds the Query Repo Folder within the workspace
     * @param {Number} folderId - The id of the folder we are searching for
     * @param {Object} workspace - The workspace in which the folder is located within
     * @return {Object} - The folder we are searching for
     */
    findQueryRepoFolder (folderId, workspace) {
      const { children = [] } = workspace
      let queryFolder = children.find(child => child.folder && child.folder.id === folderId)

      if (queryFolder) {
        return queryFolder
      } else {
        for (const child of children) {
          queryFolder = this.findQueryRepoFolder(folderId, child)

          if (queryFolder) {
            break
          }
        }

        return queryFolder
      }
    },

    /**
     * Finds the Query Repo File within the workspace
     * @param {Number} fileId - The id of the file we are searching for
     * @param {Object} workspace - The workspace in which the file is located within
     * @return {Object} - The file we are searching for
     */
    findQueryRepoFile (fileId, workspace) {
      const { children = [] } = workspace
      let queryFile = children.find(child => child.query && child.query.id === fileId)

      if (queryFile) {
        return queryFile
      } else {
        for (const child of children) {
          queryFile = this.findQueryRepoFile(fileId, child)

          if (queryFile) {
            break
          }
        }

        return queryFile
      }
    },

    /**
     * Retrieves the file/folder node within the workspace, given a path
     * @param {Object} fileNode
     * @param {Array} treePath Path of the selected node.
     * @param {Number} pathIndex Index of the tree path for validations.
     * @return {Object}
     */
    getFileNodeInfo (fileNode, treePath, pathIndex) {
      const childIndex = treePath[pathIndex]

      if (!fileNode || !fileNode.children || childIndex === undefined) {
        // Given invalid combination of parameters that is impossible to find
        // the correct file node with. So, return undefined
        return undefined
      } else if (pathIndex === treePath.length - 1) {
        return fileNode.children[childIndex]
      } else {
        return this.getFileNodeInfo(fileNode.children[childIndex], treePath, pathIndex + 1)
      }
    },

    /**
     * Retrieves the folder node that is closest to the path given. For example, if
     * a path leads to a file node, the function will return the folder node that contains
     * that file node.
     * @param {Object} folderNode
     * @param {Array} treePath - Array of indexes that formulate the path
     * @param {Number} pathIndex - The index to indicate at what point of the path are we located at
     */
    getClosestFolderNodeInfo (folderNode, treePath, pathIndex) {
      const childIndex = treePath[pathIndex]

      if (childIndex === undefined) {
        return folderNode
      }

      const childNode = folderNode.children[childIndex]

      if (childNode.isLeaf) {
        return folderNode
      } else {
        return this.getClosestFolderNodeInfo(childNode, treePath, pathIndex + 1)
      }
    },

    /**
     * Sorts children nodes of folder recursively
     * @param {Object} folderNode - The folder whose children are being sorted
     */
    sortChildrenNodesRecursive (folderNode) {
      this.sortChildrenNodes(folderNode)
      folderNode.children.forEach(child => {
        if (!child.isLeaf) {
          this.sortChildrenNodesRecursive(child)
        }
      })
    },

    /**
     * Sorts the children nodes of a folder (NOT RECURSIVELY).
     * Notes: The sort will be first by folders and then by files.
     * @param {Array} folderNode
     */
    sortChildrenNodes (folderNode) {
      const nodesStructure = {
        folders: [],
        files: []
      }
      folderNode.children.sort((a, b) => {
        if (a.linkedToRepo) return -1
        if (b.linkedToRepo) return 1

        return a.title > b.title ? 1 : (a.title < b.title ? -1 : 0)
      })
      folderNode.children.forEach(node => {
        if (!node.isLeaf) {
          nodesStructure.folders.push(node)
        } else {
          nodesStructure.files.push(node)
        }
      })
      folderNode.children = [...nodesStructure.folders, ...nodesStructure.files]
    },

    /**
     * Sorts the Query Repo Folders
     * @param {Array} folders - The folders to be sorted
     */
    sortQueryRepoFolders (folders) {
      folders.sort((a, b) => {
        const fixedFolderPriority = this.queryRepoFixedFolderPriority(a.folder, b.folder)

        if (fixedFolderPriority !== 0) {
          return fixedFolderPriority
        }
        if (a.title > b.title) {
          return 1
        }
        if (a.title < b.title) {
          return -1
        }

        return 0
      })
    },

    /**
     * Sorts the Query Repo Files
     * @param {Array} files - The files to be sorted
     */
    sortQueryRepoFiles (files) {
      files.sort((a, b) => {
        if (a.title < b.title) {
          return -1
        }
        if (a.title > b.title) {
          return 1
        }

        return 0
      })
    },

    /**
     * Returns the priority for the fixed folders on the Shared Workspace
     * @param {Object} a - Folder A
     * @param {Object} b - Folder B
     * @return {Number} - The priority. -1 means a has priority. 1 means b has priority. 0 means they have equal priority
     */
    queryRepoFixedFolderPriority (a, b) {
      const nameA = a.name.toLowerCase()
      const nameB = b.name.toLowerCase()

      if (nameA === 'users' && !a.editable) {
        return -1
      }
      if (nameB === 'users' && !b.editable) {
        return 1
      }
      if (nameA === 'teams' && !a.editable) {
        return -1
      }
      if (nameB === 'teams' && !b.editable) {
        return 1
      }
      if (nameA === 'projects' && !a.editable) {
        return -1
      }
      if (nameB === 'projects' && !b.editable) {
        return 1
      }

      return 0
    },

    /**
     * Toggles the column details preview.
     * @param {Boolean} showDetails
     * @param {Number} columnPosition  Defines the column top position in order to know where to display the preview.
     * @param {Object} columnData      Contains the column properties: name, dataType, description.
     */
    toggleColumnDetails (showDetails, columnPosition, columnData) {
      if (!columnPosition || !columnData) {
        return
      }

      const parentTop = this.$refs.queryPanel.getBoundingClientRect().top
      const details = {
        title: columnData.title,
        isQuery: false,
        content: {
          dataType: columnData.dataType,
          description: columnData.description
        }
      }

      const dataParam = {
        showPreview: showDetails,
        topPosition: columnPosition - parentTop,
        previewDetails: details,
        parentTop
      }
      this.setPreviewOptions(dataParam)
    },

    /**
     * Temporary Helper Method used to update the preview props for data catalog preview
     * This method is supposed to be sync with method `onUpdatePreviewProps`
     * The only difference is dataCatalog returns a property of `topPosition` which defines the relative postion of the child dom
     * While history preview determines the relative position given the reference to the child dom
     * TODO: changes should be made once dataCatalog / history is decoupled from query panel as well
     */
    setPreviewOptions ({ showPreview, topPosition, previewDetails, parentTop }) {
      const isEnoughHeight = topPosition + this.dataCatalogPreviewData.height + parentTop < window.innerHeight
      let horizontalPosition = ''
      if (isEnoughHeight || (!isEnoughHeight && topPosition < this.dataCatalogPreviewData.height)) {
        horizontalPosition = 'right-start'
        this.$set(this.dataCatalogPreviewData, 'referredTopPosition', topPosition)
      } else {
        horizontalPosition = 'right-end'
        const newPosition = topPosition - this.dataCatalogPreviewData.height - (previewDetails.isQuery ? 32 : 24)
        this.$set(this.dataCatalogPreviewData, 'referredTopPosition', newPosition)
      }
      const height = previewDetails.isQuery ? 300 : 112
      const show = showPreview
      const title = previewDetails.title || ''
      const isQuery = previewDetails.isQuery
      const content = previewDetails.content || {}
      const queryText = previewDetails.isQuery && previewDetails.query.hasOwnProperty('queryText') ? previewDetails.query.queryText : ''
      this.$set(content, 'queryText', queryText)
      const dataParam = { height, horizontalPosition, show, title, isQuery, content }
      this.dataCatalogPreviewData = { ...this.dataCatalogPreviewData, ...dataParam }
    },

    /**
     * Shows an specific content according with the selected panel option.
     * @param {String} panelName
     */
    showPanel (panelName) {
      if (panelName === 'scheduled') {
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueries', this.userId)
        this.$store.dispatch('scheduledQuery/onLoadScheduledQueriesSubscriptions', this.userId)
      }
      this.currentPanel = panelName
      this.$set(this.$root.preferences.queryPad, 'currentPanelTab', panelName)
    }
  }
}
</script>

<style scoped>
  .dialog-window {
    text-align: center;
  }

  .button-icon {
    padding-right: 8px;
    font-size: 16px
  }

  .spin-icon-load {
    animation: ani-demo-spin 1s linear infinite;
  }

  .time {
    font-weight: bold;
    padding-bottom: 4px;
  }

  .content span {
    display: block;
    cursor: pointer;
    padding: 2px 0;
  }

  .library-item {
    display: block;
    padding: 8px 0;
    font-weight: bold;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .content span:hover,
  .library-list span:hover {
    font-weight: bold;
    color: var(--color-blue-tabs-active);
    user-select: none;
    cursor: pointer;
    background-color: var(--color-light-gray)
  }

  .query-panel h3 {
    text-align: center;
    padding: 8px 8px 0;
    font-size: 19px;
  }

  .item-icon {
    width: 22px;
    height: 16px;
    display: inline-block;
    text-align: center;
  }

  .item-icon.toggle {
    width: 10px;
  }

  .item-icon.file {
    width: 14px;
  }

  .panel-main {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
  }

  .panel-main .panel-icon,
  .panel-main .panel-content {
    height: 100%;
  }

  .panel-main .panel-icon {
    padding-top: 2px;
    width: 32px;
    background-color: var(--color-light-gray);
    display: flex;
    flex-direction: column;
  }

  .panel-main .panel-content {
    width: calc(100% - 32px);
    padding: 0 4px;
  }

  .panel-main .panel-icon .icon-block {
    margin: 0;
    display: flex;
    justify-content: center;
  }

  .panel-main .panel-icon .icon-block .action-icon {
    padding: 8px 0;
    margin: 0 auto;
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-gray-button);
    cursor: pointer;
  }

  .panel-main .panel-icon .icon-block.active .action-icon {
    color: var(--color-black);
  }

  .error {
    margin-top: 8px;
    padding: 0 4px;
    color: var(--color-red-border);
    text-align: justify;
  }

  .query-panel >>> .ivu-tabs-bar {
    margin-bottom: 0;
  }

  .query-panel >>> .ivu-tabs-content .ivu-scroll-container {
    padding: 8px;
  }

  .tabpane-items >>> .ivu-timeline-item-head-custom {
    background-color: transparent;
  }

  #schemaTreeScroll >>> .ivu-scroll-content {
    height: calc(100% - 20px);
  }

  i {
    cursor: pointer;
  }

  .timezone {
    color: var(--color-gray);
  }
</style>
